import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import {
  USAGE_BADGE_VARIANT,
  USAGE_BAR_CLASS,
} from "@/utils/subscription.util";

import type { IPlanResource } from "../types";
import { PlanResourceIcon } from "./PlanResourceIcon";

interface PlanUsageRowProps {
  resource: IPlanResource;
  /** Entrance stagger, in ms. */
  delay?: number;
}

/** One metered resource: mark, value + context line, then the usage bar. */
export const PlanUsageRow = ({ resource, delay = 0 }: PlanUsageRowProps) => (
  <div
    className="animate-rise-in space-y-2.5 px-4 py-3.5"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-primary/10">
        <PlanResourceIcon
          resource={resource.key}
          size={resource.key === "tokens" ? 20 : 16}
          className="text-primary"
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[10px] font-semibold tracking-widest text-muted-foreground">
          {resource.label}
        </span>
        <span className="truncate text-[13px] leading-none font-bold tabular-nums text-foreground">
          {resource.value}
        </span>
      </span>

      <Badge
        variant={USAGE_BADGE_VARIANT[resource.status]}
        className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold normal-case tabular-nums"
      >
        {resource.percentLabel}
      </Badge>
    </div>

    <div
      className="h-1.5 overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-label={`${resource.label} — ${resource.percentLabel}`}
      aria-valuenow={resource.percentage}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-500 ease-out",
          USAGE_BAR_CLASS[resource.status],
        )}
        style={{ width: `${resource.percentage}%` }}
      />
    </div>

    {resource.meta && (
      <p className="text-[11px] font-medium text-muted-foreground">
        {resource.meta}
      </p>
    )}
  </div>
);
